import { documentsAPI } from '../../api/documents';
import { store } from '../../store';
import { documentsApi } from '../../store/api/documentsApi';

const DISK_DEFAULT = process.env.REACT_APP_CALL_RECORD_DISK || 'nyc3';

/**
 * POST …/attachments via RTK Query.
 * Multipart: file, model_id, model_type, disk
 */
export async function uploadRecordingAttachmentFile(
  file,
  { modelId, modelType = 'CallRecord' } = {}
) {
  if (modelId == null || modelId === '') {
    throw new Error('model_id is required for attachment upload');
  }
  const fd = new FormData();
  fd.append('file', file);
  fd.append('model_id', 1);
  fd.append('model_type', modelType);
  fd.append('disk', DISK_DEFAULT);
  return store.dispatch(documentsApi.endpoints.uploadAttachment.initiate(fd)).unwrap();
}

/**
 * Chunked upload using existing Resumable.js integration.
 * Falls back to regular upload if resumable init/upload fails.
 */
export async function uploadRecordingAttachmentFileChunked(
  file,
  { modelId, modelType = 'CallRecord', onProgress } = {}
) {
  if (modelId == null || modelId === '') {
    throw new Error('model_id is required for attachment upload');
  }

  const withTimeout = (promise, ms, label) =>
    new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error(label)), ms);
      promise
        .then((v) => {
          clearTimeout(t);
          resolve(v);
        })
        .catch((e) => {
          clearTimeout(t);
          reject(e);
        });
    });

  try {
    const r = await withTimeout(
      documentsAPI.initializeResumable({
      query: {
        model_type: modelType,
        model_id: String(modelId),
        disk: DISK_DEFAULT,
      },
      maxFiles: 1,
      simultaneousUploads: 1,
      testChunks: false,
      }),
      5000,
      'Chunk uploader initialization timed out'
    );

    return await new Promise((resolve, reject) => {
      let settled = false;
      let uploadStarted = false;
      const done = (cb) => (arg) => {
        if (settled) return;
        settled = true;
        try {
          r.cancel();
        } catch (_) {
          // no-op
        }
        cb(arg);
      };

      r.on('fileProgress', (rf) => {
        uploadStarted = true;
        if (typeof onProgress === 'function') onProgress(rf.progress());
      });
      r.on('uploadStart', () => {
        uploadStarted = true;
      });
      r.on(
        'fileSuccess',
        (rf, message) => {
          if (typeof onProgress === 'function') onProgress(1);
          const body = typeof message === 'string' ? JSON.parse(message) : message;
          done(resolve)(body);
        }
      );
      r.on(
        'fileError',
        (rf, message) => done(reject)(new Error(typeof message === 'string' ? message : 'Chunk upload failed'))
      );
      r.on('error', (message) => done(reject)(new Error(typeof message === 'string' ? message : 'Chunk upload failed')));

      r.addFile(file);
      r.upload();

      // Fail fast if chunk upload never starts (common when CDN/script/init stalls).
      setTimeout(() => {
        if (!settled && !uploadStarted) {
          done(reject)(new Error('Chunk upload did not start in time'));
        }
      }, 6000);

      // Absolute timeout even if uploadStart fired but chunks stall forever.
      setTimeout(() => {
        if (!settled) {
          done(reject)(new Error('Chunk upload timed out'));
        }
      }, 20000);
    });
  } catch (_) {
    return uploadRecordingAttachmentFile(file, { modelId, modelType });
  }
}

/**
 * Attachment POST response shape varies — align with DocsStep handleFileSuccess extraction.
 */
export function extractAttachmentIdFromUploadResponse(body) {
  if (body == null) return null;
  const d = body?.data ?? body;
  const inner = d?.data ?? d;
  return (
    inner?.id ??
    inner?.uuid ??
    body?.id ??
    d?.id ??
    inner?.attachment?.id ??
    d?.attachment?.id ??
    inner?.attachment_id ??
    d?.attachment_id ??
    null
  );
}
